import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import { imageSrc } from "../../../lib/api";
import { NOISE } from "../shaders";

const MAX = 24;
// Paint on the canvas, drawn from its history: pours spreading, a gentle flow, every drag and blow, and tilt.
const PAINT = `
uniform sampler2D uMap;
uniform float uT, uFlow, uSettle;
uniform vec2 uTiltOff;
uniform vec4 uImp[${MAX}];
uniform vec4 uImpB[${MAX}];
uniform vec4 uPour[4];
uniform vec3 uPourCol[4];
varying vec2 vUv;
${NOISE}
void main(){
  vec2 q = vUv;
  q += vec2(snoise(vec3(q * 3.0, uT * 0.08)), snoise(vec3(q * 3.0 + 7.0, uT * 0.08))) * 0.045 * uFlow;
  q += vec2(sin(q.y * 7.0 + uT * 0.5), cos(q.x * 6.0 - uT * 0.4)) * 0.02 * uFlow;
  for (int i = 0; i < ${MAX}; i++){
    vec4 b = uImpB[i];
    if (b.x <= 0.0 || uT < b.x) continue;
    float k = smoothstep(0.0, 0.6, uT - b.x);
    vec2 d = q - uImp[i].xy;
    float g = exp(-dot(d, d) / 0.012);
    if (b.y < 0.5) q -= uImp[i].zw * g * 0.16 * k;
    else q -= d / (length(d) + 0.02) * g * 0.07 * k;
  }
  q -= uTiltOff;
  vec3 col = vec3(0.984, 0.973, 0.953);
  for (int i = 0; i < 4; i++){
    vec4 p = uPour[i];
    float age = uT - p.w;
    if (age < 0.0) continue;
    float r = max(p.z * smoothstep(0.0, 1.8, age), 0.01);
    float e = snoise(vec3(q * 8.0, float(i) * 3.1)) * 0.035 + snoise(vec3(q * 22.0, float(i))) * 0.012;
    float dd = length((q - p.xy) * vec2(1.0, 0.8)) + e;
    float a = 1.0 - smoothstep(r - 0.025, r + 0.005, dd);
    float rim = smoothstep(r - 0.06, r, dd) * a;
    col = mix(col, uPourCol[i] * (1.0 - 0.15 * rim), a * 0.92);
  }
  vec3 paint = texture2D(uMap, mix(vec2(0.02), vec2(0.98), vUv)).rgb;
  float n = snoise(vec3(vUv * 6.0, 2.0)) * 0.5 + 0.5;
  float m = smoothstep(0.0, 0.3, uSettle * 1.3 - n * 0.3);
  col = mix(col, paint, m);
  col += pow(max(0.0, 1.0 - abs(vUv.x - vUv.y * 0.6 - 0.2 - 0.25 * sin(uT * 0.2)) * 3.0), 6.0) * 0.06;
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}`;

const vecs = (n) => Array.from({ length: n }, () => new THREE.Vector4());

export function MedleyCanvas({ painting, arc, clock, grab }) {
  const src = useTexture(imageSrc(painting.image_url));
  const C = arc.canvas;
  const mat = useMemo(() => {
    const t = src.clone();
    t.colorSpace = THREE.SRGBColorSpace;
    t.needsUpdate = true;
    return new THREE.ShaderMaterial({
      vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: PAINT,
      uniforms: {
        uMap: { value: t }, uT: { value: 0 }, uFlow: { value: 0 }, uSettle: { value: 0 }, uTiltOff: { value: new THREE.Vector2() },
        uImp: { value: vecs(MAX) }, uImpB: { value: vecs(MAX) },
        uPour: { value: C.pours.map((p) => new THREE.Vector4(...p.at, p.r, p.t)) },
        uPourCol: { value: C.pours.map((p) => new THREE.Color(p.color)) },
      },
    });
  }, [src, C]);
  const tilt = useRef(new THREE.Vector2());
  const last = useRef(null);
  const n = useRef(0);
  useEffect(() => {
    const on = (e) => {
      if (e.beta == null) return;
      tilt.current.set(THREE.MathUtils.clamp((e.gamma || 0) / 40, -1, 1), THREE.MathUtils.clamp(-((e.beta || 0) - 35) / 40, -1, 1));
      window.__afaeArcTilt = true;
    };
    window.addEventListener("deviceorientation", on);
    const up = () => {
      grab.current = false;
      last.current = null;
    };
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("deviceorientation", on);
      window.removeEventListener("pointerup", up);
      mat.dispose();
    };
  }, [mat, grab]);
  const playing = () => {
    const t = clock.current?.t ?? 0;
    return t >= C.play[0] && t < C.play[1];
  };
  // the history keeps its first MAX strokes, so earlier play never snaps back
  const impulse = (uv, d, kind) => {
    if (n.current >= MAX) return;
    const k = n.current++;
    mat.uniforms.uImp.value[k].set(uv.x, uv.y, d.x, d.y);
    mat.uniforms.uImpB.value[k].set(clock.current?.t ?? 0, kind, 0, 0);
    window.__afaeArcTaps = (window.__afaeArcTaps || 0) + 1;
  };
  useFrame((_, delta) => {
    const t = clock.current?.t ?? 0;
    const U = mat.uniforms;
    const ss = THREE.MathUtils.smoothstep;
    U.uT.value = t;
    U.uFlow.value = ss(t, C.pours[0].t, C.pours[0].t + 3) * (1 - ss(t, C.settle[0], C.settle[1]) * 0.85);
    U.uSettle.value = ss(t, C.settle[0], C.settle[1]);
    if (playing()) U.uTiltOff.value.addScaledVector(tilt.current, Math.min(delta, 0.05) * 0.04).clampLength(0, 0.25);
  });
  const down = (e) => {
    if (!playing() || !e.uv) return;
    e.stopPropagation();
    grab.current = true;
    last.current = { uv: e.uv.clone(), moved: false };
  };
  const move = (e) => {
    const L = last.current;
    if (!L || !grab.current || !e.uv) return;
    const d = e.uv.clone().sub(L.uv);
    if (d.length() < 0.03) return;
    L.moved = true;
    impulse(L.uv, d.clampLength(0, 0.25), 0);
    L.uv = e.uv.clone();
  };
  const tap = (e) => {
    const L = last.current;
    if (L && !L.moved && playing() && e.uv) impulse(e.uv, new THREE.Vector2(), 1);
  };
  return (
    <group position={[0, C.y, C.z]}>
      <mesh position={[0, -0.04, 0]}>
        <boxGeometry args={[C.w + 0.9, 0.06, C.h + 0.7]} />
        <meshBasicMaterial color="#6E4A36" toneMapped={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} material={mat} onPointerDown={down} onPointerMove={move} onPointerUp={tap}>
        <planeGeometry args={[C.w, C.h]} />
      </mesh>
    </group>
  );
}
